import { io, Socket } from "socket.io-client";

type RealtimeHandler<T = unknown> = (payload: T) => void;

const DEFAULT_REALTIME_EVENTS = [
  "credits.updated",
  "merchant.credits.updated",
  "account.updated",
  "integration.account.updated",
  "connection.updated",
] as const;

let socket: Socket | null = null;
let refCount = 0;

function getRealtimeUrl(): string | null {
  const url = import.meta.env.VITE_REALTIME_URL || import.meta.env.VITE_ISIGHT_URL;
  return typeof url === "string" && url.trim() ? url.trim() : null;
}

function getSocket(): Socket | null {
  const url = getRealtimeUrl();
  if (!url) return null;

  if (!socket) {
    socket = io(url, {
      transports: ["websocket"],
      reconnectionAttempts: 5,
      timeout: 15000,
      autoConnect: true,
    });
  }

  return socket;
}

function sameShop(payload: unknown, shop?: string | null): boolean {
  if (!shop) return true;
  if (!payload || typeof payload !== "object") return true;

  const payloadShop = (payload as { shop?: unknown; merchant_shop?: unknown }).shop ??
    (payload as { merchant_shop?: unknown }).merchant_shop;

  return !payloadShop || payloadShop === shop;
}

export function subscribeRealtime<T = unknown>(
  handlers: Partial<Record<(typeof DEFAULT_REALTIME_EVENTS)[number] | string, RealtimeHandler<T>>>,
  opts: { shop?: string | null } = {},
): () => void {
  const activeSocket = getSocket();
  if (!activeSocket) return () => undefined;

  refCount += 1;

  const wrappedHandlers = Object.entries(handlers).map(([event, handler]) => {
    const wrapped = (payload: T) => {
      if (sameShop(payload, opts.shop)) {
        handler?.(payload);
      }
    };
    activeSocket.on(event, wrapped);
    return { event, wrapped };
  });

  return () => {
    wrappedHandlers.forEach(({ event, wrapped }) => activeSocket.off(event, wrapped));
    refCount = Math.max(0, refCount - 1);

    if (refCount === 0) {
      activeSocket.disconnect();
      socket = null;
    }
  };
}
